import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { FaUserCircle } from 'react-icons/fa';
import { LuMap } from 'react-icons/lu';
import { updateProfileImage } from '../../redux/features/authSlice';
import { useChat } from '../../chat/ChatProvider';
import { fetchProfileSummary } from '../../utils/shared/profileSummaryCache';
import MilletWordmark from '../brand/MilletWordmark';
import ChatNotificationButton from './ChatNotificationButton';
import HeaderMarketplaceSearch from './HeaderMarketplaceSearch';
import HeaderMarketplaceCategoryFilters from './HeaderMarketplaceCategoryFilters';
import useMarketplaceChromeSearch from '../../hooks/marketplace/useMarketplaceChromeSearch';
import useGuestAccessPrompt from '../../hooks/auth/useGuestAccessPrompt';
import GuestBrandAccessDialog from '../auth/GuestBrandAccessDialog';
import useHideCreatorMapEntryPoints from '../../hooks/navigation/useHideCreatorMapEntryPoints';
import { CREATOR_MAP_PATH } from '../../utils/navigation/creatorMapVisibility';
import {
  NavIconHome,
  NavIconDashboard,
  NavIconBrowse,
  NavIconMap,
  NavIconPricing,
  NavIconMegaphone,
  NavIconBlog,
} from './NavDropdownIcons';
import {
  HeaderWrapper,
  HeaderMainRow,
  HeaderCategoryRow,
  LogoContainer,
  LogoLink,
  HeaderGuestNav,
  HeaderGuestLink,
  HeaderSearchSlot,
  AuthButtons,
  ProfileIcon,
  HeaderOffset,
  ProfileDropdown,
  NotificationButton,
  HeaderActions,
  HeaderActionsDivider,
  HeaderIconButton,
  ProfileActions,
  ProfileActionsCluster,
  LoginLink,
  TooltipWrapper,
  Tooltip,
} from '../../styles/navigation/HeaderStyles';
import {
  MARKETPLACE_DETAIL_HERO_CHROME_MQL,
  shouldHideSiteHeaderOnMobile,
  shouldShowBottomNavigation,
} from '../../utils/navigation/bottomNavigationVisibility';

const MOBILE_HEADER_MAX_WIDTH_PX = 800;

// Helper to determine active className based on path matching
const getActiveClassName = (path, currentPath, isExact = false) => {
  if (isExact) {
    return currentPath === path ? 'active' : '';
  }
  return currentPath.startsWith(path) ? 'active' : '';
};

const getProfileDropdownLinks = (userType, { includeCreatorMap = true } = {}) => {
  if (userType === 'admin') return [];
  return [
    { path: '/', label: 'Home', icon: NavIconHome, isExact: true },
    {
      path: '/dashboard/overview',
      label: 'Dashboard',
      icon: NavIconDashboard,
    },
    userType === 'influencer' && {
      path: '/marketplace/campaigns',
      label: 'Campaigns',
      icon: NavIconMegaphone,
    },
    userType === 'brand' && {
      path: '/marketplace/influencers',
      label: 'Browse Influencers',
      icon: NavIconBrowse,
    },
    userType === 'brand' && includeCreatorMap && {
      path: CREATOR_MAP_PATH,
      label: 'Influencer Map',
      icon: NavIconMap,
      isExact: true,
    },
    userType === 'brand' && {
      path: '/pricing',
      label: 'Pricing',
      icon: NavIconPricing,
      isExact: true,
    },
    (userType === 'influencer' || userType === 'brand') && {
      path: '/blog',
      label: 'Blog',
      icon: NavIconBlog,
    },
  ].filter(Boolean);
};

// Utility to create navigation handler with scroll-to-top behavior
const createNavigateHandler = (navigate, onStateReset) => (path) => (event) => {
  event.preventDefault();
  if (onStateReset) onStateReset();

  // Always scroll to top immediately (instant scroll for immediate effect)
  window.scrollTo(0, 0);

  navigate(path);

  // Also ensure scroll happens after navigation completes (for smooth behavior)
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, 50);
};

function Header({ isAuthenticated, showAuthButtons = true }) {
  const isMobileView = useMediaQuery(`(max-width:${MOBILE_HEADER_MAX_WIDTH_PX}px)`);
  const isDetailHeroChromeViewport = useMediaQuery(MARKETPLACE_DETAIL_HERO_CHROME_MQL);
  const useCompactHeaderLogo = useMediaQuery('(max-width: 744px)');
  const hideCreatorMapEntryPoints = useHideCreatorMapEntryPoints();
  const [userType, setUserType] = useState(null);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    prompt: guestPrompt,
    openPrompt: openGuestPrompt,
    closePrompt: closeGuestPrompt,
    handleLogin: handleGuestLogin,
    handleSignUp: handleGuestSignUp,
  } = useGuestAccessPrompt();
  const {
    draft: searchDraft,
    setDraft: setSearchDraft,
    category: searchCategory,
    platforms: searchPlatforms,
    contentTypes: searchContentTypes,
    saved: searchSaved,
    followingBrands: searchFollowingBrands,
    selectCategory: selectSearchCategory,
    selectPlatform: selectSearchPlatform,
    selectContentFormat: selectSearchContentFormat,
    selectSaved: selectSearchSaved,
    selectBrandsYouFollow: selectSearchBrandsYouFollow,
    clearFacets: clearSearchFacets,
    submit: submitSearch,
    clear: clearSearch,
    selectSuggestion,
    placeholder: searchPlaceholder,
    hideMarketplaceFacets,
  } = useMarketplaceChromeSearch(userType, { onGuestAction: openGuestPrompt });

  // Get chat functionality (hook must be called unconditionally)
  const chatContext = useChat();
  const toggleChat = chatContext?.toggle || (() => {});
  const unreadCount = chatContext?.unreadCount || 0;

  const reduxProfileImageUrl = useSelector((state) => state.auth.profileImageUrl);

  const fetchProfilePicture = async (currentUserType) => {
    if (reduxProfileImageUrl) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const summary = await fetchProfileSummary(currentUserType);
      if (summary.avatarUrl) {
        setProfileImageUrl(summary.avatarUrl);
        dispatch(updateProfileImage(summary.avatarUrl));
      }
    } catch (error) {
      console.error('Error fetching profile picture:', error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserType(payload.user_type);

        if (payload.user_type === 'brand' || payload.user_type === 'influencer') {
          fetchProfilePicture(payload.user_type);
        }
      } catch (error) {
        console.error('Invalid token format:', error);
        localStorage.removeItem('token'); // Remove invalid token
      }
    } else {
      setUserType(null); // Reset userType if not authenticated
      setProfileImageUrl(null); // Reset profile picture
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]); // Removed reduxProfileImageUrl and dispatch to prevent loops, cache handles throttling

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownOpen && !event.target.closest('.profile-menu-container')) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [profileDropdownOpen]);

  const showBottomNav = shouldShowBottomNavigation({

    pathname: location.pathname,
    isAuthenticated,
    userType,
  });
  // Bottom-tab routes: hide site header ≤800px. Listing details: only while floating
  // hero chrome is active (≤750px / below influencerSidebar) so 751–800px keeps Header.
  const hideForMobileBottomNav = (isMobileView && showBottomNav)
    || (isDetailHeroChromeViewport && shouldHideSiteHeaderOnMobile(location.pathname));
  const handleNavigate = createNavigateHandler(navigate, () => setProfileDropdownOpen(false));

  const handleLogoClick = (event) => {
    event.preventDefault();
    setProfileDropdownOpen(false);

    // If already on home page, reload the page
    if (location.pathname === '/') {
      window.location.href = '/';
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate('/');
    }
  };

  const profileDropdownLinks = getProfileDropdownLinks(userType, {
    includeCreatorMap: !hideCreatorMapEntryPoints,
  });
  const showHeaderSearch = Boolean(isAuthenticated) && userType !== 'admin';

  return (
    <>
      <HeaderOffset
        $hideForMobileBottomNav={hideForMobileBottomNav}
      />
      <HeaderWrapper
        $hideForMobileBottomNav={hideForMobileBottomNav}
      >
        <HeaderMainRow>
          <LogoContainer>
            <LogoLink href="/" onClick={handleLogoClick} aria-label="millet home">
              <MilletWordmark
                variant={useCompactHeaderLogo ? 'mark' : 'wordmark'}
                size="header"
                className="logo-wordmark"
                aria-hidden
              />
            </LogoLink>
            {!isAuthenticated && (
              <HeaderGuestNav aria-label="Marketplace">
                <HeaderGuestLink
                  href="/marketplace/influencers"
                  onClick={handleNavigate('/marketplace/influencers')}
                >
                  Browse Influencers
                </HeaderGuestLink>
                {!hideCreatorMapEntryPoints && (
                  <HeaderGuestLink
                    href={CREATOR_MAP_PATH}
                    onClick={handleNavigate(CREATOR_MAP_PATH)}
                  >
                    Influencer Map
                  </HeaderGuestLink>
                )}
                <HeaderGuestLink href="/blog" onClick={handleNavigate('/blog')}>
                  Blog
                </HeaderGuestLink>
                <HeaderGuestLink href="/pricing" onClick={handleNavigate('/pricing')}>
                  Pricing
                </HeaderGuestLink>
              </HeaderGuestNav>
            )}
          </LogoContainer>

          {showHeaderSearch ? (
            <HeaderSearchSlot>
              <HeaderMarketplaceSearch
                value={searchDraft}
                onChange={setSearchDraft}
                onSubmit={submitSearch}
                onClear={clearSearch}
                onSelectSuggestion={selectSuggestion}
                onSelectCategory={selectSearchCategory}
                onSelectPlatform={selectSearchPlatform}
                onSelectContentFormat={selectSearchContentFormat}
                onBrowseAllCategories={clearSearchFacets}
                userType={userType}
                placeholder={searchPlaceholder}
                inputId="header-marketplace-search"
                hideMarketplaceFacets={hideMarketplaceFacets}
              />
            </HeaderSearchSlot>
          ) : null}
          {showAuthButtons && (
            <AuthButtons>
              {isAuthenticated ? (
                <ProfileActions className="profile-menu-container">
                  <ProfileActionsCluster>
                    {userType === 'admin' ? null : (
                      <HeaderActions>
                        {userType === 'brand' && !hideCreatorMapEntryPoints && (
                          <TooltipWrapper>
                            <HeaderIconButton
                              onClick={handleNavigate(CREATOR_MAP_PATH)}
                              aria-label="Open influencer map"
                              type="button"
                            >
                              <span className="header-action-btn-icon">
                                <LuMap aria-hidden strokeWidth={1.75} />
                              </span>
                            </HeaderIconButton>
                            <Tooltip>Influencer Map</Tooltip>
                          </TooltipWrapper>
                        )}
                        <ChatNotificationButton
                          toggleChat={toggleChat}
                          unreadCount={unreadCount}
                          NotificationButton={NotificationButton}
                          TooltipWrapper={TooltipWrapper}
                          Tooltip={Tooltip}
                        />
                      </HeaderActions>
                    )}

                    <HeaderActionsDivider aria-hidden />
                    <TooltipWrapper $alignRight $suppress={profileDropdownOpen}>
                      <ProfileIcon
                        onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                        aria-label="Open navigation menu"
                        aria-expanded={profileDropdownOpen}
                        aria-haspopup="menu"
                        type="button"
                      >
                        {reduxProfileImageUrl || profileImageUrl ? (
                          <img
                            src={reduxProfileImageUrl || profileImageUrl}
                            alt=""
                            className="profile-picture"
                            onError={(e) => {
                              console.error('Failed to load profile image');
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <FaUserCircle className="profile-icon-svg" />
                        )}
                      </ProfileIcon>
                      <Tooltip $alignRight>Navigation</Tooltip>
                    </TooltipWrapper>
                  </ProfileActionsCluster>
                  <ProfileDropdown $isOpen={profileDropdownOpen}>
                    <ul>
                      {profileDropdownLinks.map((link) => {
                        const DropdownIcon = link.icon;
                        return (
                          <li
                            key={link.path}
                            className={getActiveClassName(link.path, location.pathname, link.isExact)}
                          >
                            <a href={link.path} onClick={handleNavigate(link.path)}>
                              <DropdownIcon className="dropdown-icon" />
                              {link.label}
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  </ProfileDropdown>
                </ProfileActions>
              ) : (
                <ProfileActions>
                  <LoginLink href="/login" onClick={handleNavigate('/login')}>
                    Sign in
                  </LoginLink>
                </ProfileActions>
              )}
            </AuthButtons>
          )}
        </HeaderMainRow>

        {userType === 'admin' ? null : (
          <HeaderCategoryRow>
            <HeaderMarketplaceCategoryFilters
              category={searchCategory}
              platforms={searchPlatforms}
              contentTypes={searchContentTypes}
              saved={searchSaved}
              followingBrands={searchFollowingBrands}
              onSelectCategory={selectSearchCategory}
              onSelectPlatform={selectSearchPlatform}
              onSelectContentFormat={selectSearchContentFormat}
              onSelectSaved={selectSearchSaved}
              onSelectBrandsYouFollow={selectSearchBrandsYouFollow}
              onClearFacets={clearSearchFacets}
              userType={userType}
            />
          </HeaderCategoryRow>
        )}
      </HeaderWrapper>
      <GuestBrandAccessDialog
        open={Boolean(guestPrompt)}
        intent={guestPrompt?.intent || 'saveCreator'}
        onClose={closeGuestPrompt}
        onLogin={handleGuestLogin}
        onSignUp={handleGuestSignUp}
      />
    </>
  );
}

export default Header;
